import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { PrimaryButton } from '@fluentui/react';
import { BestellungStatusLabel, getBestellungLabel } from 'utils/labels';
import { formStyles } from 'styles/formStyles';
import { Bestellung } from 'api/bestellung';
import { Timestamps } from 'formComponents/Timestamps';
import { BestellungPosition } from 'api/bestellungPosition';
import {
    betreuerByNrSelector,
    lagerByIntNrSelector,
    skontodefinitionenByArtSelector,
    textbausteineByKzNrSelector,
} from 'store/lookups/selector';
import { formatMoney, formatSmDate } from 'utils/formatter';
import { authSelector } from 'store/auth/selector';
import nl2br from 'react-nl2br';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { BestellungPositionen } from './BestellungPositionen';

const styles = mergeStyleSets({
    shortLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '120px 120px 1fr 100px max-content',
        gridColumnGap: 10,
    },
    status: {
        textAlign: 'right',
    },
});

interface BestellungItemProps {
    bestellung: Bestellung;
    bestellungPositionen: BestellungPosition[];
    withKunde?: boolean;
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    editBestellung: () => void;
    // refetch: () => void;
}

export const BestellungItem: React.FC<BestellungItemProps> = ({
    bestellung,
    bestellungPositionen,
    withKunde,
    viewMode,
    withHeader = true,
    editBestellung,
}) => {
    const { kunde } = bestellung;

    const auth = useSelector(authSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const textbausteineByKzNr = useSelector(textbausteineByKzNrSelector);
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);
    const lagerByIntNr = useSelector(lagerByIntNrSelector);

    const history = useHistory();

    if (!kunde) {
        return null;
    }

    const renderTimestamps = () => (
        <Timestamps
            createdDate={bestellung.anlageDat}
            createdDecimalTime={bestellung.anlageUhr}
            createdUser={bestellung.anlageUser}
            modifiedDate={bestellung.updDat}
            modifiedDecimalTime={bestellung.updUhr}
            modifiedUser={bestellung.updUser}
        />
    );

    const renderStatus = () => <div className={styles.status}>{BestellungStatusLabel[bestellung.status]}</div>;

    const renderEditButton = () => (
        <div>
            {(auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX') && (
                <PrimaryButton
                    className={viewMode === 'short' ? formStyles.smallButton : ''}
                    onClick={() => {
                        editBestellung();
                    }}
                    text="Bearbeiten"
                />
            )}
        </div>
    );

    const containerClassName = classnames(viewMode === 'short' ? formStyles.listItemShort : formStyles.listItem, {
        [formStyles.bestellungInArbeit]: bestellung.status === 0,
        [formStyles.bestellungOffen]: bestellung.status === 10,
        [formStyles.bestellungVersendet]: bestellung.status === 1,
        [formStyles.bestellungTeillieferung]: bestellung.status === 8,
        [formStyles.bestellungAbgeschlossen]: bestellung.status === 4,
    });

    if (viewMode === 'short') {
        return (
            <div className={containerClassName}>
                <div className={styles.shortLine}>
                    <div className={formStyles.shortValue}>
                        <strong>
                            {bestellung.nr} {bestellung.abrufZu ? '(Abr)' : ''}
                            {bestellung.rahmen ? '(Ra)' : ''}
                        </strong>
                    </div>
                    <div className={formStyles.shortValue}>
                        <strong>{formatSmDate(bestellung.datum)}</strong>
                    </div>

                    {withKunde ? (
                        <div
                            className={classnames(formStyles.listTitleShort, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                            }}
                        >
                            {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    ) : (
                        <div />
                    )}

                    {renderStatus()}
                    {renderEditButton()}
                </div>

                <div>
                    <BestellungPositionen
                        kunde={kunde}
                        bestellung={bestellung}
                        bestellungPositionen={bestellungPositionen}
                        viewMode={viewMode}
                        withHeader={withHeader}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={containerClassName}>
                <div className={formStyles.topBlock}>
                    <div>
                        <div
                            className={classnames(formStyles.listTitle, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                                // openDetails();
                            }}
                        >
                            {getBestellungLabel(bestellung, kunde)}
                        </div>
                    </div>
                    {renderStatus()}
                    {renderEditButton()}
                </div>
                <div className={formStyles.fourColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.line}>
                            {kunde.plz} {kunde.ort}, {kunde.strasse} {kunde.land && `(${kunde.land})`}
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ansprechpartner:</div>
                            <div className={formStyles.dataValue}>{bestellung.kaName}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Bestell-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{bestellung.nr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferanten-R.Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{bestellung.nrExt}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{bestellung.auNr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Abruf zu:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{bestellung.abrufZu}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ausstellungsdat:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(bestellung.datum)}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Best. gültig bis:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(bestellung.datBis)}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {bestellung.betreuer &&
                                        (betreuerByNr[bestellung.betreuer]
                                            ? betreuerByNr[bestellung.betreuer].name || bestellung.betreuer
                                            : bestellung.betreuer)}
                                </strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Rahmenbestellung:</div>
                            <div className={formStyles.dataValue}>{bestellung.rahmen ? 'Ja' : 'Nein'}</div>
                        </div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lager:</div>
                            <div className={formStyles.dataValue}>
                                {bestellung.lagerIntNr
                                    ? lagerByIntNr[bestellung.lagerIntNr]
                                        ? lagerByIntNr[bestellung.lagerIntNr].name || bestellung.lagerIntNr
                                        : bestellung.lagerIntNr
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Text:</div>
                            <div className={formStyles.dataValue}>{nl2br(bestellung.txt)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 1:</div>
                            <div className={formStyles.dataValue}>
                                {bestellung.txtB1
                                    ? textbausteineByKzNr[23][bestellung.txtB1]
                                        ? textbausteineByKzNr[23][bestellung.txtB1].name || bestellung.txtB1
                                        : bestellung.txtB1
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 2:</div>
                            <div className={formStyles.dataValue}>
                                {bestellung.txtB2
                                    ? textbausteineByKzNr[23][bestellung.txtB2]
                                        ? textbausteineByKzNr[23][bestellung.txtB2].name || bestellung.txtB2
                                        : bestellung.txtB2
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 3:</div>
                            <div className={formStyles.dataValue}>
                                {bestellung.txtB3
                                    ? textbausteineByKzNr[23][bestellung.txtB3]
                                        ? textbausteineByKzNr[23][bestellung.txtB3].name || bestellung.txtB3
                                        : bestellung.txtB3
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 4:</div>
                            <div className={formStyles.dataValue}>
                                {bestellung.txtB4
                                    ? textbausteineByKzNr[23][bestellung.txtB4]
                                        ? textbausteineByKzNr[23][bestellung.txtB4].name || bestellung.txtB4
                                        : bestellung.txtB4
                                    : '-'}
                            </div>
                        </div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferbed.:</div>
                            <div className={formStyles.dataValue}>
                                {bestellung.liefBed
                                    ? textbausteineByKzNr[11][bestellung.liefBed]
                                        ? textbausteineByKzNr[11][bestellung.liefBed].name || bestellung.liefBed
                                        : bestellung.liefBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferzeit:</div>
                            <div className={formStyles.dataValue}>
                                {bestellung.liefZeit
                                    ? textbausteineByKzNr[14][bestellung.liefZeit]
                                        ? textbausteineByKzNr[14][bestellung.liefZeit].name || bestellung.liefZeit
                                        : bestellung.liefZeit
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferdatum.:</div>
                            <div className={formStyles.dataValue}>
                                {bestellung.liefDat && (formatSmDate(bestellung.liefDat, true) || '-')}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Liefer-KW.:</div>
                            <div className={formStyles.dataValue}>{bestellung.liefKw ? bestellung.liefKw : '-'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Zahlungsbed.:</div>
                            <div className={formStyles.dataValue}>
                                {bestellung.zahlBed
                                    ? skontodefinitionenByArt[bestellung.zahlBed]
                                        ? skontodefinitionenByArt[bestellung.zahlBed].text || bestellung.zahlBed
                                        : bestellung.zahlBed
                                    : '-'}
                            </div>
                        </div>
                    </div>
                    <div className={formStyles.fourthColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Bemerkungen (beim Lieferanten):</div>
                            <div className={formStyles.dataValue}>{nl2br(bestellung?.kunde?.bemerkung)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Lieferbemerkung (beim Lieferanten):</div>
                            <div className={formStyles.dataValue}>{nl2br(bestellung?.kunde?.liefBem)}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <BestellungPositionen
                        kunde={kunde}
                        bestellung={bestellung}
                        bestellungPositionen={bestellungPositionen}
                        viewMode={viewMode}
                    />
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.sumLine}>Summe Netto: {formatMoney(bestellung.sNetto)}</div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.sumLine}>Summe Brutto: {formatMoney(bestellung.sBrutto)}</div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.sumLine}>Summe MwSt.: {formatMoney(bestellung.sMwst)}</div>
                    </div>
                </div>
                {renderTimestamps()}
            </div>
            {/* showCopy && (
                <KundeSelect
                    label="Kunde"
                    kNr={bestellung?.kNr}
                    name={kunde?.name1}
                    dialogOnly
                    onChange={async (newKNr) => {
                        if (bestellung && newKNr) {
                            await copyBestellung(newKNr, bestellung, bestellungPositionen);
                            refetch();
                            setShowCopy(false);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            ) */}
        </>
    );
};
